import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ShieldCheck, History, Users, Plus, KeyRound, Trash2, AlertTriangle } from 'lucide-react';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { RoleEditor } from '../components/RoleEditor';

const inputCls = 'w-full bg-muted/40 border border-border rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/50';

const Admin = () => {
  const { user: me } = useAuth();
  const isAdmin = me?.role === 'Administrator';
  const [tab, setTab] = useState('users');
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [purging, setPurging] = useState(false);

  const purge = async () => {
    setPurging(true);
    try {
      const { data } = await api.post('/admin/purge-data', { confirm: purgeConfirm });
      const total = Object.values(data.removed || {}).reduce((a, b) => a + b, 0);
      toast({ title: '🧹 Dados limpos', description: `${total} registros removidos. Mantidos: ${data.kept.users} usuários e ${data.kept.vehicles} veículos.` });
      setPurgeOpen(false); setPurgeConfirm('');
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setPurging(false); }
  };
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [editRole, setEditRole] = useState(null);
  const [logs, setLogs] = useState([]);
  const [nOpen, setNOpen] = useState(false);
  const [nForm, setNForm] = useState({ name: '', login: '', password: '', role: 'Salesperson', job_title: '', email: '', phone: '' });
  const [rpUser, setRpUser] = useState(null);
  const [rpPass, setRpPass] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [u, r, l] = await Promise.all([api.get('/users'), api.get('/roles'), api.get('/audit_logs')]);
      setUsers(u.data); setRoles(r.data); setLogs(l.data);
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (uid, role) => {
    setUsers((p) => p.map((u) => u.user_id === uid ? { ...u, role } : u));
    try { await api.put(`/users/${uid}/role`, { role }); toast({ title: 'Role atualizado', description: role }); } catch (e) { load(); }
  };

  const createUser = async () => {
    if (!nForm.name || !nForm.login || !nForm.password) { toast({ title: 'Preencha nome, login e senha' }); return; }
    setSaving(true);
    try {
      await api.post('/users', nForm);
      toast({ title: 'Usuário criado', description: `Login: ${nForm.login.toLowerCase()}` });
      setNOpen(false); setNForm({ name: '', login: '', password: '', role: 'Salesperson' });
      load();
    } catch (e) {
      toast({ title: 'Erro ao criar usuário', description: typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Tente novamente.' });
    } finally { setSaving(false); }
  };

  const resetPassword = async () => {
    if (!rpPass || rpPass.length < 6) { toast({ title: 'Senha deve ter no mínimo 6 caracteres' }); return; }
    setSaving(true);
    try {
      await api.put(`/users/${rpUser.user_id}/password`, { password: rpPass });
      toast({ title: 'Senha alterada', description: rpUser.name });
      setRpUser(null); setRpPass('');
    } catch (e) {
      toast({ title: 'Erro ao alterar senha', description: typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Tente novamente.' });
    } finally { setSaving(false); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Excluir o usuário ${u.name}?`)) return;
    try { await api.delete(`/users/${u.user_id}`); toast({ title: 'Usuário excluído', description: u.name }); load(); }
    catch (e) { toast({ title: 'Erro ao excluir', description: typeof e.response?.data?.detail === 'string' ? e.response.data.detail : '' }); }
  };

  const tabs = [{ k: 'users', label: 'Usuários', icon: Users }, { k: 'roles', label: 'Roles & Permissões', icon: ShieldCheck }, { k: 'audit', label: 'Audit Log', icon: History }];

  return (
    !isAdmin && me ? <Navigate to="/app" replace /> :
    (
    <div className="space-y-5">
      <div className="mob-chiprow flex items-center gap-2 bg-foreground/[0.03] border border-border rounded-lg p-1 w-full md:w-fit">
        {tabs.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`flex items-center gap-2 px-3.5 py-1.5 rounded-md text-sm font-medium transition-all ${tab === t.k ? 'bg-primary text-white shadow-glow' : 'text-foreground/60 hover:text-foreground'}`}><t.icon className="w-4 h-4" /> {t.label}</button>
        ))}
      </div>

      {tab === 'users' && (
        <div className="rounded-xl border border-border/70 bg-card p-6 overflow-x-auto">
          {isAdmin && (
            <div className="flex justify-end mb-4">
              <button data-testid="new-user-btn" onClick={() => setNOpen(true)} className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-all shadow-glow hover:shadow-glow-lg"><Plus className="w-4 h-4" /> Novo Usuário</button>
            </div>
          )}
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground text-xs tracking-wider border-b border-border/70"><th className="pb-3 font-semibold">USUÁRIO</th><th className="pb-3 font-semibold hidden lg:table-cell">CARGO</th><th className="pb-3 font-semibold">LOGIN</th><th className="pb-3 font-semibold hidden md:table-cell">EMAIL</th><th className="pb-3 font-semibold hidden xl:table-cell">TELEFONE</th><th className="pb-3 font-semibold">ROLE</th>{isAdmin && <th className="pb-3 font-semibold text-right">AÇÕES</th>}</tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-b border-border/60 row-hover">
                  <td className="py-3.5 font-medium flex items-center gap-2.5"><img src={u.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name || 'U')}&background=2563eb&color=fff`} alt="" className="w-8 h-8 rounded-full" referrerPolicy="no-referrer" />{u.name}</td>
                  <td className="py-3.5 text-foreground/65 hidden lg:table-cell">{u.job_title || '—'}</td>
                  <td className="py-3.5 text-foreground/65 font-mono text-xs">{u.login || '—'}</td>
                  <td className="py-3.5 text-foreground/65 hidden md:table-cell">{u.email}</td>
                  <td className="py-3.5 text-foreground/65 hidden xl:table-cell">{u.phone || '—'}</td>
                  <td className="py-3.5">
                    <select value={u.role || 'Owner'} disabled={!isAdmin} onChange={(e) => changeRole(u.user_id, e.target.value)} className="bg-muted/50 border border-border rounded-md px-2 py-1 text-xs outline-none focus:border-primary cursor-pointer disabled:opacity-50">
                      {roles.map((r) => <option key={r.name} className="bg-popover">{r.name}</option>)}
                    </select>
                  </td>
                  {isAdmin && (
                    <td className="py-3.5 text-right whitespace-nowrap">
                      <button data-testid={`reset-pass-${u.login || u.user_id}`} onClick={() => { setRpUser(u); setRpPass(''); }} title="Alterar senha" className="text-muted-foreground hover:text-foreground border border-border hover:border-foreground/25 rounded-md p-1.5 mr-2 transition-colors"><KeyRound className="w-3.5 h-3.5" /></button>
                      {u.user_id !== me?.user_id && <button data-testid={`delete-user-${u.login || u.user_id}`} onClick={() => removeUser(u)} title="Excluir" className="text-red-400/60 hover:text-red-400 border border-border hover:border-red-400/40 rounded-md p-1.5 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          <Dialog open={nOpen} onOpenChange={setNOpen}>
            <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground">
              <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">Novo Usuário</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Nome</label><input data-testid="new-user-name" className={inputCls} value={nForm.name} onChange={(e) => setNForm({ ...nForm, name: e.target.value })} placeholder="Ex: João Silva" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-muted-foreground mb-1.5 block">Cargo do funcionário</label><input data-testid="new-user-jobtitle" className={inputCls} value={nForm.job_title} onChange={(e) => setNForm({ ...nForm, job_title: e.target.value })} placeholder="Ex: Vendedor Sênior" /></div>
                  <div><label className="text-xs text-muted-foreground mb-1.5 block">Telefone</label><input data-testid="new-user-phone" className={inputCls} value={nForm.phone} onChange={(e) => setNForm({ ...nForm, phone: e.target.value })} placeholder="(508) 555-0000" /></div>
                </div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Email</label><input data-testid="new-user-email" className={inputCls} value={nForm.email} onChange={(e) => setNForm({ ...nForm, email: e.target.value })} placeholder="email@speedmotors.com" /></div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Login</label><input data-testid="new-user-login" className={inputCls} value={nForm.login} onChange={(e) => setNForm({ ...nForm, login: e.target.value })} placeholder="Ex: joao" autoComplete="off" /></div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Senha (mín. 6 caracteres)</label><input data-testid="new-user-password" type="text" className={inputCls} value={nForm.password} onChange={(e) => setNForm({ ...nForm, password: e.target.value })} placeholder="••••••" autoComplete="new-password" /></div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Role</label>
                  <select data-testid="new-user-role" className={inputCls} value={nForm.role} onChange={(e) => setNForm({ ...nForm, role: e.target.value })}>
                    {roles.map((r) => <option key={r.name} className="bg-popover">{r.name}</option>)}
                  </select>
                </div>
              </div>
              <DialogFooter>
                <button onClick={() => setNOpen(false)} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors">Cancelar</button>
                <button data-testid="new-user-save" onClick={createUser} disabled={saving} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors disabled:opacity-60">{saving ? 'Salvando...' : 'Criar Usuário'}</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={!!rpUser} onOpenChange={(o) => !o && setRpUser(null)}>
            <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground">
              <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">Alterar senha — {rpUser?.name}</DialogTitle></DialogHeader>
              <div className="py-2"><label className="text-xs text-muted-foreground mb-1.5 block">Nova senha (mín. 6 caracteres)</label><input data-testid="reset-pass-input" type="text" className={inputCls} value={rpPass} onChange={(e) => setRpPass(e.target.value)} placeholder="••••••" autoComplete="new-password" /></div>
              <DialogFooter>
                <button onClick={() => setRpUser(null)} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors">Cancelar</button>
                <button data-testid="reset-pass-save" onClick={resetPassword} disabled={saving} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors disabled:opacity-60">{saving ? 'Salvando...' : 'Salvar Senha'}</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {tab === 'roles' && (editRole ? (
        <RoleEditor roleName={editRole} onBack={() => setEditRole(null)} onSaved={load} />
      ) : (
        <div className="space-y-3">
          <p className="text-[11.5px] t-label">Clique em uma role para abrir o editor detalhado de permissões. As permissões atuais são o estado real do banco — nada é alterado até você salvar.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 stagger">
            {roles.map((r) => (
              <button key={r.name} data-testid={`role-card-${r.name.replace(/\s+/g, '-')}`} onClick={() => setEditRole(r.name)}
                className="text-left rounded-xl border border-border bg-card p-5 glow-border card-hover hover:border-primary/50 transition-colors">
                <div className="flex items-center gap-2 mb-3"><ShieldCheck className="w-4 h-4 text-primary" /><h4 className="font-semibold text-foreground">{r.name}</h4><span className="ml-auto text-[11px] text-muted-foreground">{r.permissions.length} perms</span></div>
                <div className="flex flex-wrap gap-1.5">
                  {r.permissions.slice(0, 8).map((p) => <span key={p} className="text-[10px] px-2 py-0.5 rounded bg-muted/50 text-muted-foreground">{p}</span>)}
                  {r.permissions.length > 8 && <span className="text-[10px] px-2 py-0.5 text-muted-foreground">+{r.permissions.length - 8}</span>}
                </div>
                <p className="text-[10.5px] text-primary font-semibold mt-3">Editar permissões →</p>
              </button>
            ))}
          </div>
        </div>
      ))}

      {tab === 'audit' && (
        <div className="rounded-xl border border-border/70 bg-card p-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground text-xs tracking-wider border-b border-border/70"><th className="pb-3 font-semibold">DATA/HORA</th><th className="pb-3 font-semibold">USUÁRIO</th><th className="pb-3 font-semibold">AÇÃO</th><th className="pb-3 font-semibold">ENTIDADE</th><th className="pb-3 font-semibold">DETALHES</th></tr></thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-b border-border/70">
                  <td className="py-3 text-muted-foreground">{l.timestamp}</td>
                  <td className="py-3">{l.user}</td>
                  <td className="py-3"><span className="text-[11px] px-2 py-0.5 rounded bg-muted/50 text-foreground/65">{l.action}</span></td>
                  <td className="py-3 text-foreground/65">{l.entity}</td>
                  <td className="py-3 text-muted-foreground">{l.details}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {logs.length === 0 && <div className="text-center py-14 text-muted-foreground">Sem registros de auditoria.</div>}
        </div>
      )}

      <div className="rounded-xl border border-red-500/25 bg-red-500/[0.04] p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-sm font-black uppercase text-red-400 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Zona de Perigo — Limpar Dados do Sistema</p>
            <p className="text-xs text-muted-foreground mt-1">Apaga leads, BDC, clientes, deals, vendas, tarefas, chat e agendamentos. Mantém estoque, usuários e equipe. Irreversível.</p>
          </div>
          <button data-testid="purge-open-btn" onClick={() => setPurgeOpen(true)} className="px-4 py-2.5 rounded-lg text-sm font-bold bg-red-600 hover:bg-red-700 transition-colors">Limpar Dados</button>
        </div>
      </div>

      <Dialog open={purgeOpen} onOpenChange={(o) => { setPurgeOpen(o); if (!o) setPurgeConfirm(''); }}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-md">
          <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase text-red-400">Limpar Dados do Sistema?</DialogTitle></DialogHeader>
          <p className="text-sm text-foreground/65">Isso apaga <span className="text-foreground font-semibold">permanentemente</span> todos os leads, clientes, deals, vendas, tarefas, conversas e agendamentos. Estoque, usuários e equipe são mantidos.</p>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Digite <span className="font-black text-red-400">LIMPAR</span> para confirmar</label>
            <input data-testid="purge-confirm-input" className="w-full bg-muted/40 border border-border rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-red-500 transition-colors" value={purgeConfirm} onChange={(e) => setPurgeConfirm(e.target.value)} placeholder="LIMPAR" />
          </div>
          <DialogFooter>
            <button onClick={() => setPurgeOpen(false)} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground">Cancelar</button>
            <button data-testid="purge-confirm-btn" onClick={purge} disabled={purging || purgeConfirm.trim().toUpperCase() !== 'LIMPAR'} className="px-5 py-2.5 rounded-lg text-sm font-bold bg-red-600 hover:bg-red-700 disabled:opacity-40">{purging ? 'Limpando...' : 'Confirmar Limpeza'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    )
  );
};

export default Admin;
